/**
 * Тело intake API. §Окружение 11.1.
 *
 * Форма согласована так, чтобы фикстуру можно было физически отправить: у неё нет ни темы
 * (мониторинга в фазе 1 нет), ни generation (вызовов LLM нет), ни формата (библиотека
 * форматов — фаза 2). Поэтому topic_id / format / format_rationale / generation стали
 * nullable, а origin — обязательным новым полем.
 *
 * `method` в теле нет и быть не может: его ВЫБИРАЕТ заморозка — `freezeAuthored`
 * (`canonical/canonicalize.ts`) с политикой бренда «все посты — расширенным сообщением»
 * (docs/release-2026-09-19.md §9.3): картинка, документ, текст длиннее порога или политика —
 * `sendRichMessage`, иначе `sendMessage`. Её зовут и приём на бэкенде, и сборка писателя, и
 * это одна функция на обе стороны: иначе разошёлся бы `content_hash`.
 */

import * as z from 'zod';
import { BRAND_RE } from '../approval-key/limits.ts';
import { UUID_V7_RE } from '../ids.ts';
import { LinkPreviewOptionsSchema, MessageEntitySchema } from '../telegram/draft.ts';
import { ISO_UTC_MS } from '../time.ts';
import { codePointLen } from '../units.ts';
import { IntakeDraftAuthorOpinionSchema } from './author-opinion.ts';
import { EVENT_ID_RE } from './brand-config.ts';
import { EVENT_REVIEW_FORMAT } from './knowledge.ts';
import { PosterSpecSchema } from './poster.ts';
import type { SourceOrigin } from './source-registry.ts';
import {
  VISUAL_REASON_MAX,
  VISUAL_VERDICTS,
  VisualLayoutSchema,
  VisualSpecSchema,
} from './visual.ts';

const Uuid = z.string().regex(UUID_V7_RE);
const IsoUtcMs = z.string().regex(ISO_UTC_MS);

/**
 * Длина в КОДПОИНТАХ, а не в UTF-16 code units, которыми меряет `.max()` у zod.
 * Разница видна ровно там, где текст пишет человек: заголовок из 200 эмодзи — это 200
 * символов и 400 code units, и `.max(200)` отбил бы законный ввод (§7.6, три единицы длины).
 */
export function codePointsString(min: number, max: number) {
  return codePoints(min, max);
}

function codePoints(min: number, max: number) {
  return z.string().refine(
    (s) => {
      const n = codePointLen(s) as unknown as number;
      return n >= min && n <= max;
    },
    { error: `ожидалось ${String(min)}..${String(max)} кодпоинтов` },
  );
}

export const IntakeMediaRefSchema = z.strictObject({
  /** id актива, положенного POST /intake/v1/media. */
  media_id: Uuid,
  /** Параметры вёрстки: их выбирает автор, вывести их неоткуда, и они входят в подпись. */
  show_caption_above_media: z.boolean(),
  has_spoiler: z.boolean(),
});

/**
 * Ссылка на документ версии — PDF внизу поста (docs/release-2026-09-19.md §9.2). Только id
 * актива, как у медиа: дескриптор (`mime_type`, `sha256`, `bytes`, `file_name`) бэкенд считает
 * по строке актива, и второго источника правды о файле тело не заводит.
 *
 * Документ прикрепляет ТОЛЬКО человек; писатель документов не создаёт, а НАСЛЕДУЕТ. Поэтому
 * ссылка законна ровно в одном случае — новая версия несёт документ ТЕКУЩЕЙ версии черновика
 * (`current_version.document.media_id` из `GET /intake/v1/drafts/:id`). Остальное бэкенд
 * отбивает 400 `invalid_request` с `details.reason`: у нового черновика —
 * `document_forbidden_for_new_draft`, у новой версии с другим id — `document_not_inherited`.
 */
export const IntakeDocumentRefSchema = z.strictObject({
  media_id: Uuid,
});

export const IntakeDraftBodySchema = z
  .strictObject({
    origin: z.enum(['fixture', 'writer']),
    brand: z.string().regex(BRAND_RE),
    topic_id: Uuid.nullable(),
    format: z.string().max(64).nullable(),
    format_rationale: z.string().max(2048).nullable(),
    text: z.string(),
    entities: z.array(MessageEntitySchema),
    link_preview_options: LinkPreviewOptionsSchema.nullable(),
    media: IntakeMediaRefSchema.nullable(),
    /**
     * Документ версии (§9.2) — ссылка на PDF текущей версии, который переделка НАСЛЕДУЕТ.
     * Писатель замораживает версию с его дескриптором и сверяет `content_hash` с ответом, поэтому
     * ссылка едет телом, а не приклеивается бэкендом: приклей он файл сам, хэш писателя разошёлся
     * бы на каждой переделке поста с PDF.
     *
     * КЛЮЧ НЕОБЯЗАТЕЛЕН по той же причине, что `visual`: тело прежнего писателя его не несёт, и
     * требовать ключ значило бы 400 на каждой отправке, пока бэкенд и сервис генерации не
     * встанут в одну секунду. Отсутствие ключа и `null` означают одно — «документа нет»: у
     * нового черновика это норма, у новой версии поверх версии с PDF — снятие файла, которое
     * бэкенд записывает в журнал события версии (`document_dropped`).
     */
    document: IntakeDocumentRefSchema.nullable().optional(),
    generation: z
      .strictObject({
        model: z.string().max(64),
        prompt_version: z.string().max(64),
        tokens: z.int().nonnegative(),
      })
      .nullable(),
    /**
     * РЕШЕНИЕ О КАРТИНКЕ и его причина. Едет здесь, рядом с `generation`, а не отдельным
     * маршрутом, по простой причине: вердикт рождается ДО версии, и без него интерфейсу
     * нечем показать «картинки нет — вот почему» (§6.2), а §2.6 нечего считать.
     *
     * `proposal` — это КАНДИДАТ, а не прикреплённая картинка (решение Ильи 06.09.2026,
     * §6.4 вариант «в»). Поэтому он и не может ехать в поле `media`: `media` означает
     * «эта картинка внутри версии и внутри подписи», а предложение по определению лежит
     * снаружи версии, пока человек его не принял.
     *
     * КЛЮЧ НЕОБЯЗАТЕЛЕН, и это не мягкость, а порядок выкатки. У фикстуры вердикта нет и
     * быть не может; у тела, собранного до этого контракта, его тоже нет. Требовать ключ
     * значило бы, что бэкенд с миграцией и сервис генерации обязаны встать в одну секунду,
     * иначе КАЖДЫЙ пост получает 400 — то есть цена забытого поля здесь не «поле забыли», а
     * «канал молчит». Отсутствие ключа и `null` означают одно: вердикта не выносили.
     */
    visual: z
      .strictObject({
        verdict: z.enum(VISUAL_VERDICTS),
        reason: z.string().min(1).max(VISUAL_REASON_MAX),
        proposal: z
          .strictObject({
            media_id: Uuid,
            layout: VisualLayoutSchema,
            spec: VisualSpecSchema,
            instead_of_text: z.string().min(1).max(VISUAL_REASON_MAX),
          })
          .nullable(),
        /**
         * ОБЛОЖКА ВНУТРИ ВЕРСИИ (docs/phase-2/10-posters.md, решение Ильи 2026-10-28). В
         * отличие от `proposal`, это не кандидат: байты обложки — это `media` этой же версии,
         * они внутри подписи, и утверждение поста утверждает их. Поле несёт спецификацию —
         * то, из чего картинка собрана, — чтобы бэкенд положил её рядом с байтами: по ней
         * проверяется дата при одобрении и считается чередование фонов.
         *
         * Ключ необязателен по той же причине, что и весь `visual`: тело прежнего писателя
         * его не несёт.
         */
        poster: PosterSpecSchema.nullable().optional(),
      })
      .nullable()
      .optional(),
    /**
     * Каким стилем написана версия (docs/release-2026-09-19.md §5.5) — id строки
     * `style_version`, которой писатель собрал промпт, включая релиз. Необязателен по той же
     * причине, что `visual`: у фикстуры стиля нет, а тело прежнего писателя его не несёт —
     * отсутствие и `null` значат одно: «стиль не известен».
     */
    style_version_id: Uuid.nullable().optional(),
    /** Только при origin === 'fixture'. Стабильный slug фикстуры. */
    fixture_id: z.string().max(64).optional(),
    /** Только у версии, замыкающей ветку переделки. */
    rework_id: Uuid.optional(),
    /**
     * Мнение автора канала, которое несёт ЭТА версия (AGI Boardroom, `api/author-opinion.ts`):
     * id живой строки `author_opinion` и очищенный текст в канонической форме. Бэкенд сверяет,
     * что строка этого черновика и жива, что вставку заказала эта переделка, и что очищенный
     * текст стоит в каноническом тексте версии дословно (`authorOpinionSurvives`) — иначе 422
     * `author_opinion_lost`.
     *
     * Ключ необязателен по той же причине, что `visual`: тело прежнего писателя его не несёт, и
     * у постов без мнения его нет. Мнение бывает только у переделки (его добавляют к готовому
     * посту), поэтому без `rework_id` и у фикстуры ключ — отказ схемы.
     */
    author_opinion: IntakeDraftAuthorOpinionSchema.optional(),
  })
  .check((ctx) => {
    const v = ctx.value;
    const push = (path: string, message: string): void => {
      ctx.issues.push({ code: 'custom', input: v, path: [path], message });
    };

    if (v.origin === 'fixture') {
      if (v.topic_id !== null) push('topic_id', 'topic_id_must_be_null_for_fixture');
      if (v.format !== null) push('format', 'format_must_be_null_for_fixture');
      if (v.format_rationale !== null)
        push('format_rationale', 'format_rationale_must_be_null_for_fixture');
      if (v.generation !== null) push('generation', 'generation_must_be_null_for_fixture');
      if (v.visual !== null && v.visual !== undefined) {
        push('visual', 'visual_must_be_null_for_fixture');
      }
      if (v.author_opinion !== undefined) {
        push('author_opinion', 'author_opinion_forbidden_for_fixture');
      }
      if (v.fixture_id === undefined) push('fixture_id', 'fixture_id_required');
    } else {
      if (v.topic_id === null) push('topic_id', 'topic_id_required_for_writer');
      if (v.format === null) push('format', 'format_required_for_writer');
      if (v.format_rationale === null)
        push('format_rationale', 'format_rationale_required_for_writer');
      if (v.generation === null) push('generation', 'generation_required_for_writer');
      if (v.fixture_id !== undefined) push('fixture_id', 'fixture_id_forbidden_for_writer');
      // Мнение добавляют к готовому посту, поэтому несёт его только версия переделки: новый
      // черновик с мнением — ошибка писателя, а не ещё один путь вставки.
      if (v.author_opinion !== undefined && v.rework_id === undefined) {
        push('author_opinion', 'author_opinion_requires_rework_id');
      }
    }

    // Карточка задаётся автором ЯВНО и ровно тогда, когда картинки нет: «карточки не
    // будет» — это `is_disabled: true`, а не `null` (§5.1). У поста с картинкой карточки не
    // бывает вовсе, и опции обязаны быть `null`.
    //
    // ДЛИНУ здесь не смотрят и смотреть не могут: порог 4064 считается по КАНОНИЧЕСКОМУ
    // тексту, а схема видит сырой. Длинный пост тоже приезжает с карточкой, и снимает её
    // `freezeAuthored` уже после канонизации, предупреждением, а не отказом. Проверять
    // длину здесь значило бы отбивать законное тело: 4070 знаков с хвостовыми пробелами —
    // это 4060 после канонизации, то есть обычное сообщение с карточкой.
    //
    // ДОКУМЕНТ (§9.2) этого правила не меняет по той же причине: пост с PDF уходит rich, но
    // решает это `freezeAuthored`, а не схема, — автор поста без картинки говорит о карточке
    // явно и тогда, когда внизу файл, и снимается она предупреждением.
    if ((v.media !== null) === (v.link_preview_options !== null)) {
      push('link_preview_options', 'lpo_method_mismatch');
    }

    // Вердикт и предложение — одно утверждение, а не два поля. «Нарисовал» без карточки и
    // «не рисовал» с карточкой — это состояния, которых не бывает; разрешить их значило бы
    // отдать интерфейсу строку, которая противоречит кнопке рядом с ней.
    const vis = v.visual ?? null;
    const poster = vis?.poster ?? null;
    if (vis !== null && (vis.verdict === 'drawn') !== (vis.proposal !== null || poster !== null)) {
      push('visual', 'visual_verdict_proposal_mismatch');
    }
    // Обложка внутри и предложение инфографики рядом — законная пара: человек нажал
    // «Нарисовать картинку» на посте с обложкой, и принять предложение значит заменить ею
    // обложку (`acceptVisual` кладёт новое медиа в v+1), отклонить — оставить обложку.
    //
    // Обложка — это байты ВЕРСИИ. Спецификация без `media` описывала бы картинку, которой в
    // подписываемом контенте нет, и полоса визуала показала бы то, что не уйдёт в канал.
    if (poster !== null && v.media === null) {
      push('visual', 'visual_poster_without_media');
    }
    if (vis !== null && vis.proposal !== null) {
      // Макет назван дважды — полем `layout` и дискриминантом `spec.kind`. Поле нужно
      // интерфейсу (подпись полосы читается без разбора спецификации), но два имени одного
      // не бывает: расхождение здесь — это полоса, подписанная не тем макетом.
      if (vis.proposal.layout !== vis.proposal.spec.kind) {
        push('visual', 'visual_layout_spec_mismatch');
      }
    }
  });

export type IntakeDraftBody = z.output<typeof IntakeDraftBodySchema>;

/* ──────────────────────────────── темы ──────────────────────────────────── */

/**
 * Материалов у темы 1..10, и ровно один из них ведущий. docs/phase-2/05-seams.md §1.2.
 *
 * Было 5, стало 8 ради сводки недели (`weekly-digest`, правка 7 от 2026-09-08): сводка —
 * это ОДНА тема из верхушки недельного ранжирования, где каждый пункт несёт свой материал,
 * а «пять новостей за неделю» — это не сводка, а короткий список. С релиза 2026-10-14
 * (docs/release-2026-10-14-news-digest.md) — 10: «Дайджест недели» держал 5–10 пунктов за
 * пять дней, пункт = материал. С 2026-10-16 дайджест держит 3–5 (потолок мониторинга
 * `DIGEST_MATERIALS_MAX`), а 10 остаётся потолком любой темы и запасом для тем дайджеста,
 * заведённых раньше. Потолок остаётся, потому что материалы едут в бриф целиком:
 * без него один прогон унёс бы в промпт всё, что нашёл. Число общее у мониторинга и приёма
 * бэкенда — выкатываются вместе, иначе тема из 9–10 материалов получает 400.
 */
export const TOPIC_MATERIALS_MAX = 10;
/** Потолок ОДНОЙ выжимки, кодпоинты. Переезжает в конфиг бренда (`evidence_excerpt_max`). */
export const EVIDENCE_EXCERPT_MAX = 6000;
/**
 * Потолок СУММЫ выжимок одной темы, кодпоинты. Отдельное число, а не N×6000: выжимки едут
 * в бриф генерации целиком, и ограничивать надо то, что попадёт в промпт, а не отдельный
 * фрагмент. Проверяется в маршруте (413), а не схемой: 400 сказал бы «тело кривое» там,
 * где тело правильное, а данных слишком много.
 *
 * ПОЧЕМУ ОН НЕ ПОДНЯТ ВМЕСТЕ С `TOPIC_MATERIALS_MAX`. Арифметика теперь такая: 10 × 6000 =
 * 60 000 при сумме 12 000, то есть с десятью материалами СУММА — связывающее ограничение
 * раньше, чем потолок одной выжимки, — и это правильный порядок. Сводке нужен ЛИД каждого
 * пункта, а не десять статей целиком; 12 000 на десять материалов — это 1200 кодпоинтов на
 * пункт, чего лиду хватает с запасом.
 *
 * Зажимать выжимку под этот потолок обязан МОНИТОРИНГ (он единственный видит, сколько
 * материалов кладёт в тему, и режет по своим правилам — сводке лид, обычной теме статью).
 * Здесь потолок только объявлен: маршрут отдаёт 413, а не молча обрезает чужой текст.
 */
export const EVIDENCE_EXCERPT_TOTAL_MAX = 12_000;

/**
 * Сырой адрес материала. Канонизирует его бэкенд — клиент присылает то, что видел.
 * Разбираемость проверяется здесь, а не в канонизаторе: `https://` проходит регулярку и
 * падает в `new URL`, и отказ должен быть 400 со схемой, а не 500 из глубины.
 */
const SourceUrl = z
  .string()
  .min(1)
  .max(2048)
  .refine((u) => /^https?:\/\//i.test(u) && URL.canParse(u), {
    error: 'url must be a parsable http(s):// address',
  });

export const IntakeTopicMaterialSchema = z.strictObject({
  role: z.enum(['primary', 'supporting']),
  /** Код источника из реестра. В подфазе R1 всегда null: реестра ещё нет. */
  source_code: z.string().max(64).nullable(),
  url: SourceUrl,
  guid: z.string().max(512).nullable(),
  /** СЫРОЙ заголовок, как пришёл. Нормализованный считает бэкенд. */
  title: codePoints(1, 400),
  /** Лид — вход simhash. Тоже сырой. */
  lead: codePoints(1, 4000),
  /** Фрагмент статьи, вырезанный мониторингом, без переписывания моделью (§1.5 Р-C1.6). */
  excerpt: codePoints(1, EVIDENCE_EXCERPT_MAX).nullable(),
  published_at: IsoUtcMs.nullable(),
  /** Декларация, а не вход: разобрал ли клиент дату из фида или подставил своё время. */
  published_at_origin: z.enum(['source', 'fetched_at']),
  fetched_at: IsoUtcMs,
});

export type IntakeTopicMaterial = z.output<typeof IntakeTopicMaterialSchema>;

/**
 * Тело POST /intake/v1/topics. docs/phase-2/05-seams.md §1.2, форма нормативна.
 *
 * Полей отпечатка (`fingerprint`, `dedup_fingerprint`, `title_norm`, `simhash_hi/lo`) здесь
 * НЕТ и быть не может: это ключи уникальности (`ux_topic_dedup`, `UNIQUE(brand_code,
 * fingerprint)`), а ключ, посчитанный клиентом, означает, что баг клиента молча плодит
 * дубликаты вместо 409. Считает их бэкенд — теми же функциями `@smm/contracts`, которыми
 * мониторинг отсеивает виденное до трат на префильтр.
 *
 * `origin` клиент задаёт, но бэкенд сверяет его с сервисом токена (Р-C1.3): `smm-monitor`
 * вправе прислать только `monitoring`, `smm-writer` — только `evergreen`. `fixture` через
 * HTTP не принимается никогда — его пишет `tools/seed-fixtures.ts` мимо этого маршрута.
 */
export const IntakeTopicBodySchema = z
  .strictObject({
    brand: z.string().regex(BRAND_RE),
    /** null законен: тема, заведённая человеком, прогона не имеет по смыслу (Р-C1.4). */
    run_id: Uuid.nullable(),
    origin: z.enum(['monitoring', 'manual', 'evergreen']),

    title: codePoints(1, 200),
    angle: codePoints(0, 500).nullable(),
    /** ОДНА строка обоснования — образ результата Б2. */
    rationale: codePoints(1, 500),
    /** Код формата из библиотеки Б1.5. */
    format_hint: z.string().max(64).nullable(),
    score: z.number().min(0).max(1).nullable(),

    /** Часы. Не миллисекунды: величина живёт в конфиге бренда и её пишет человек (§7.3). */
    freshness_max_age_hours: z.int().positive().max(8760).nullable(),
    freshness_policy: z.enum(['dated', 'evergreen']),

    materials: z.array(IntakeTopicMaterialSchema).min(1).max(TOPIC_MATERIALS_MAX),

    /**
     * Выпуск конференции, о котором тема обзора (`format_hint: 'event-review'`, AGI Boardroom,
     * 2026-11-02). По нему бэкенд проверяет, что выпуск в списке наблюдения бренда
     * (`event_watch`, 422 иначе), и считает отпечаток дедупа `event-review:<id>:<год>` — один
     * обзор на выпуск в год. Выпуск бывает только у темы обзора: у темы новости он — тема,
     * которую писатель напишет новостью.
     *
     * Обязателен у темы обзора, которую завёл МОНИТОРИНГ (`origin: 'monitoring'`): обзор без
     * выпуска нечем дедуплицировать. Тема прогона по контексту («Задать контекст», §5: редактор
     * вставил анонсы сам, мониторинга нет) — тема писателя (`origin: 'evergreen'`,
     * `contextTopicBody`) с `format_hint: 'event-review'` и БЕЗ выпуска: выпуск никто не
     * определял, и дедуп выпуска к ней не относится. Проверку списка наблюдения и отпечаток
     * выпуска бэкенд применяет только к теме, у которой ключ есть; тема без него дедуплицируется
     * как любая другая (ревью 2026-11-02, находка 14).
     *
     * Необязателен по той же причине, что новые поля тела писателя: тело прежнего мониторинга
     * его не несёт.
     */
    event: z.strictObject({ id: z.string().regex(EVENT_ID_RE) }).optional(),
  })
  .check((ctx) => {
    const v = ctx.value;
    const review = v.format_hint === EVENT_REVIEW_FORMAT;
    if (v.event !== undefined && !review) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['event'],
        message: 'event_only_for_event_review',
      });
    }
    if (review && v.event === undefined && v.origin === 'monitoring') {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['event'],
        message: 'event_required_for_event_review',
      });
    }
    // Ровно один ведущий материал: составной PK `topic_source_ref` этого не выражает,
    // а от ведущего берутся и дата темы, и её отпечаток (§1.4, §1.5).
    const primaries = v.materials.filter((m) => m.role === 'primary').length;
    if (primaries !== 1) {
      ctx.issues.push({
        code: 'custom',
        input: v,
        path: ['materials'],
        message: 'exactly_one_primary_material_required',
      });
    }
  });

export type IntakeTopicBody = z.output<typeof IntakeTopicBodySchema>;

/** Что бэкенд сделал с одним присланным материалом. */
export interface TopicMaterialCreated {
  readonly role: 'primary' | 'supporting';
  readonly material_id: string;
  readonly url_canonical: string;
  readonly published_at: string;
  /** false — материал уже был: одна новость, подтверждающая две темы, это норма. */
  readonly created: boolean;
}

/**
 * Ответ 201. `url_canonical` и `published_at` возвращаются намеренно: отправитель обязан
 * увидеть, ЧТО система сделала с его вводом, — иначе он не узнает, что дату ему обрезали
 * до `fetched_at`, а из адреса вырезали `utm_*`.
 */
export interface TopicCreated {
  readonly topic_id: string;
  readonly status: 'proposed';
  readonly freshness_deadline: string | null;
  /**
   * Отпечаток дедупа, под которым тема ЗАПИСАНА. `null` — тема статьи, выбранной человеком в
   * таблице прогона (docs/release-2026-09-19.md §3.4): дедуп её не держит.
   */
  readonly dedup_fingerprint: string | null;
  readonly materials: readonly TopicMaterialCreated[];
}

export type TopicStatus = 'proposed' | 'selected' | 'dropped' | 'expired';

/**
 * Причина, по которой сервис вправе снять ВЗЯТУЮ тему (`selected → dropped`), решение
 * 2026-09-21. Без причины сервис снимает только свободную тему: взятая и не дописанная
 * возвращается в оборот, потому что сбой генерации обычно проходит сам. Отказ
 * классификатора модели (`stop_reason: refusal`) не проходит: сметающий проход брал такую
 * тему каждые 15 минут и собирал новый отказ на аккаунт при каждой попытке.
 *
 * `digest_unfit` (релиз 2026-10-14): дайджест недели, чья форма (ссылки пунктов на
 * `url_canonical` материалов, число пунктов) не починилась переписывающим проходом. Возврат в
 * оборот означал бы ту же петлю — платную серию писателя каждые 15 минут до конца срока темы;
 * снятая тема освобождает неделю дайджеста (`topic.dedup_released`), и повтор делает человек.
 */
export const TOPIC_DROP_REASONS = ['model_refusal', 'digest_unfit'] as const;
export type TopicDropReason = (typeof TOPIC_DROP_REASONS)[number];
export const isTopicDropReason = (v: string): v is TopicDropReason =>
  (TOPIC_DROP_REASONS as readonly string[]).includes(v);

export interface TopicMaterialView {
  readonly role: 'primary' | 'supporting';
  readonly material_id: string;
  readonly source_code: string | null;
  readonly url_canonical: string;
  readonly title: string | null;
  readonly lead: string | null;
  readonly excerpt: string | null;
  readonly published_at: string;
  /**
   * Голос источника материала (`source.origin`, реестр — `SOURCE_ORIGINS`): `official` —
   * организация о себе, `media` — пересказ. Писатель ставит ссылку ВНУТРИ текста только на
   * официальный материал (`source_link: 'inline'`). Необязательно: бэкенд до 2026-11-02 поля не
   * отдаёт, и материал без него — `media` (`sourceOriginOf`): без пометки ссылки не будет, а не
   * будет чужой.
   */
  readonly source_origin?: SourceOrigin;
}

/**
 * Голос источника материала темы с умолчанием `media`: и для ответа бэкенда старше поля, и для
 * материала без строки реестра (ссылка редактора). Одно правило у всех читателей вида темы.
 */
export function sourceOriginOf(m: Pick<TopicMaterialView, 'source_origin'>): SourceOrigin {
  return m.source_origin ?? 'media';
}

/** Ровно то, что нужно брифу генерации. docs/phase-2/05-seams.md §1.6. */
export interface TopicView {
  readonly topic_id: string;
  readonly brand: string;
  readonly status: TopicStatus;
  readonly origin: 'monitoring' | 'manual' | 'evergreen' | 'fixture';
  readonly title: string;
  readonly angle: string | null;
  readonly rationale: string | null;
  readonly format_hint: string | null;
  readonly score: number | null;
  readonly source_published_at: string | null;
  readonly freshness_deadline: string | null;
  readonly freshness_policy: 'dated' | 'evergreen';
  readonly created_at: string;
  readonly materials: readonly TopicMaterialView[];
  /**
   * Хосты правила ссылок этой темы (`linkPolicyHosts`, @smm/contracts/preview): домены бренда +
   * домен первоисточника по реестру. Писатель сверяет с ними версию ДО отправки тем же
   * `linkPolicyViolations`, что и приём (bug review 2026-10-18, №7). Необязательно: бэкенд до
   * 2026-10-19 поля не отдаёт, и писатель тогда проверки не делает.
   */
  readonly link_hosts?: readonly string[];
}

/** Список — тот же объект без `lead` и `excerpt`: он не должен весить мегабайты. */
export type TopicListMaterial = Omit<TopicMaterialView, 'lead' | 'excerpt'>;

export interface TopicListItem extends Omit<TopicView, 'materials'> {
  readonly materials: readonly TopicListMaterial[];
}

export interface TopicListPage {
  readonly items: readonly TopicListItem[];
  readonly next_cursor: string | null;
}

/**
 * Ответ перехода темы — состояние, в котором тема ОКАЗАЛАСЬ. `proposed` — возврат в оборот
 * (F-054); с P3 возврат с отказом может закончиться и `dropped`: снять тему решает политика
 * бэкенда, а не сервис.
 */
export interface TopicStatusChanged {
  readonly topic_id: string;
  readonly status: 'selected' | 'dropped' | 'proposed';
}

/**
 * Ответ маршрута медиа. `width`/`height` закрывают долг фазы 1: `04-backend-http.md:775`
 * описывает ответ как `{media_id, sha256, bytes, width, height}` с самого начала, а колонки
 * до сих пор писались NULL. Значения СПРАВОЧНЫЕ: в подписанный дескриптор они не входят и
 * ниже по течению им не верят — размеры там читают из самих байтов.
 */
export const IntakeMediaResponseSchema = z.strictObject({
  media_id: z.string().regex(UUID_V7_RE),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  bytes: z.int().positive(),
  mime_type: z.enum(['image/png', 'image/jpeg']),
  width: z.int().positive().nullable(),
  height: z.int().positive().nullable(),
});

/** ^[A-Za-z0-9._:-]{16,64}$ — непрозрачный токен; форма ULID ему ничего не даёт,
 *  а детерминированный ключ засева фикстур в неё не укладывается. */
export const IDEMPOTENCY_KEY_RE = /^[A-Za-z0-9._:-]{16,64}$/;

/** Потолок тела маршрута медиа: 8 МиБ. Совпадает с потолком скачивания у публикатора. */
export const MEDIA_MAX_BYTES = 8_388_608;
/** Общий bodyLimit остальных маршрутов. */
export const BODY_LIMIT_BYTES = 262_144;
